import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { API, auth } from "./shared";
import { TOK } from "../../pages/Soulumo/SignatureBits";
import { GolfPackButton } from "./GolfPackButton";

const SERIF = '"Cormorant Garamond", serif';
const SANS = '"Manrope", sans-serif';

const Card = ({ it, i, onPick }) => (
  <motion.button type="button" onClick={() => onPick(it)} data-testid={`golf-welcome-item-${i}`}
    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 + i * 0.09 }}
    className="group w-full text-left rounded-2xl px-5 py-4 transition-[background-color,border-color] duration-200 hover:bg-white/[0.04]"
    style={{ border: `1px solid ${TOK.goldMist}` }}>
    <span className="flex items-center justify-between gap-3">
      <span className="text-xl" style={{ fontFamily: SERIF, color: TOK.cream }}>{it.title}</span>
      <ArrowRight size={15} color={TOK.gold} className="shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
    </span>
    <span className="mt-1 block text-xs line-through" style={{ color: TOK.creamMist, fontFamily: SANS }}>"{it.before}"</span>
    <span className="block text-base italic" style={{ color: TOK.gold, fontFamily: SERIF }}>"{it.after}"</span>
  </motion.button>
);

// Shown once to members who joined through a golf or gym partner: five moments, each opens Create in their own words.
export const GolfWelcome = ({ onPick, preview }) => {
  const [w, setW] = useState(null);
  useEffect(() => {
    const q = preview ? "?preview=true" : "";
    const v = preview === "gym" ? ["gym"] : preview ? ["golf"] : ["golf", "gym"];
    (async () => {
      for (const k of v) {
        const r = await axios.get(`${API}/api/${k}-partner/welcome${q}`, auth()).catch(() => null);
        if (r?.data?.show) return setW({ ...r.data, v: k });
      }
    })();
  }, [preview]);
  if (!w) return null;
  const close = () => { setW(null); axios.post(`${API}/api/${w.v}-partner/welcome/dismiss`, {}, auth()).catch(() => {}); };
  const pick = (it) => { close(); onPick(it.intent); };
  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[130] overflow-y-auto" style={{ background: "radial-gradient(ellipse at 50% 0%, #16294a 0%, #0d1f3a 45%, #070f1e 100%)" }} data-testid="golf-welcome">
      <div className="max-w-lg mx-auto px-6 pt-16 pb-12">
        <p className="text-[0.62rem] uppercase tracking-[0.38em]" style={{ color: TOK.gold, fontFamily: SANS }}>{w.eyebrow}</p>
        <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="mt-4 text-4xl font-light leading-[1.1]" style={{ fontFamily: SERIF, color: TOK.cream }} data-testid="golf-welcome-headline">{w.headline}</motion.h2>
        <p className="mt-5 text-base leading-relaxed" style={{ color: TOK.creamDim, fontFamily: SANS }}>{w.copy}</p>
        <div className="mt-8 space-y-2">{w.items.map((it, i) => <Card key={it.title} it={it} i={i} onPick={pick} />)}</div>
        {w.v === "golf" && <GolfPackButton className="mt-6" />}
        <button type="button" onClick={close} data-testid="golf-welcome-skip" className="mt-8 w-full text-center text-[0.62rem] uppercase tracking-[0.28em] py-3" style={{ color: TOK.creamMist, fontFamily: SANS }}>{w.skip}</button>
      </div>
    </motion.div>,
    document.body,
  );
};
